import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Package, MessageSquare, Calendar, Star, Plus, Eye, CheckSquare, TrendingUp } from 'lucide-react';
import AdminLayout from '../Common/AdminLayout';
import './AdminDashBoard.css';
import SecondaryFooter from '../Common/SecondaryFooter';
import {
  getDashboardStats,
  getRecentActivity
} from "../../service/dashboard.service";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [analyticsData, setAnalyticsData] = useState({
    totalProducts: 0,
    pendingReviews: 0,
    demoRequestsToday: 0,
    demoRequestsTotal: 0,
    testimonialsCount: 0
  });

  const [recentActivityData, setRecentActivityData] = useState({
    demoRequests: [],
    pendingReviews: [],
    recentProducts: []
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [stats, activity] = await Promise.all([
          getDashboardStats(),
          getRecentActivity()
        ]);

        setAnalyticsData(stats);
        setRecentActivityData(activity);

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };

    fetchDashboardData();
  }, []);

  const metricsCards = [
    {
      title: 'Total Products',
      value: analyticsData.totalProducts,
      icon: Package,
      gradient: 'dashboard-gradient-blue',
      link: '/admin/products',
    },
    {
      title: 'Pending Reviews',
      value: analyticsData.pendingReviews,
      icon: MessageSquare,
      gradient: 'dashboard-gradient-amber',
      link: '/admin/draft-reviews',
      trend: 'Needs attention'
    },
    {
      title: 'Demo Requests Today',
      value: analyticsData.demoRequestsToday,
      subtitle: `Total: ${analyticsData.demoRequestsTotal}`,
      icon: Calendar,
      gradient: 'dashboard-gradient-emerald',
      link: '/admin/demo-requests',
    },
    {
      title: 'Testimonials',
      value: analyticsData.testimonialsCount,
      icon: Star,
      gradient: 'dashboard-gradient-violet',
      link: '/admin/testimonials',
      trend: 'Active'
    }
  ];

  const actionButtons = [
    { label: 'Add Product', icon: Plus, link: '/admin/products', style: 'dashboard-action-primary' },
    { label: 'Add Testimonial', icon: Plus, link: '/admin/testimonials', style: 'dashboard-action-secondary' },
    { label: 'View Demo Requests', icon: Eye, link: '/admin/demo-requests', style: 'dashboard-action-success' },
    { label: 'Review Pending', icon: CheckSquare, link: '/admin/draft-reviews', style: 'dashboard-action-warning' }
  ];

  const handleRouteNavigation = (link) => {
    navigate(link);
  };

  return (
    <AdminLayout>
      {/* Page Header */}
      <header className="dashboard-page-header">
        <div className="header-content">
          <h1 className="header-title">Dashboard Overview</h1>
          <p className="header-subtitle">Monitor your SEO platform performance and manage key metrics</p>
        </div>
        <div className="header-actions">
          <button className="header-refresh-btn" onClick={() => window.location.reload()}>
            <TrendingUp size={18} />
            <span>Refresh Data</span>
          </button>
        </div>
      </header>

      {/* Metrics Overview Grid */}
      <section className="dashboard-metrics-section">
        <div className="metrics-grid">
          {metricsCards.map((metric, index) => (
            <article
              key={index}
              className="metric-card"
              onClick={() => handleRouteNavigation(metric.link)}
              tabIndex={0}
              role="button"
            >
              <div className="metric-card-header">
                <div className={`metric-icon-wrapper ${metric.gradient}`}>
                  <metric.icon size={24} className="metric-icon" />
                </div>
                <span className="metric-trend">{metric.trend}</span>
              </div>
              <div className="metric-card-body">
                <h3 className="metric-title">{metric.title}</h3>
                <p className="metric-value">{metric.value}</p>
                {metric.subtitle && <p className="metric-subtitle">{metric.subtitle}</p>}
              </div>
              <div className="metric-card-footer">
                <span className="metric-link-text">View details →</span>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Quick Actions Panel */}
      <section className="dashboard-actions-section">
        <h2 className="section-heading">Quick Actions</h2>
        <div className="actions-button-grid">
          {actionButtons.map((action, index) => (
            <button
              key={index}
              className={`action-button ${action.style}`}
              onClick={() => handleRouteNavigation(action.link)}
            >
              <action.icon size={20} className="action-icon" />
              <span className="action-label">{action.label}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Activity Monitor Section */}
      <section className="dashboard-activity-section">
        <h2 className="section-heading">Recent Activity Monitor</h2>

        <div className="activity-panels-grid">
          {/* Demo Requests Panel */}
          <div className="activity-panel">
            <div className="panel-header">
              <h3 className="panel-title">Latest Demo Requests</h3>
              <span className="panel-badge">{recentActivityData.demoRequests?.length || 0}</span>
            </div>
            <div className="panel-content">
              {recentActivityData.demoRequests && recentActivityData.demoRequests.length > 0 ? (
                recentActivityData.demoRequests.map((request) => (
                  <div
                    key={request.id}
                    className="activity-entry"
                    onClick={() => handleRouteNavigation(`/admin/demo-requests/${request.id}`)}
                    tabIndex={0}
                    role="button"
                  >
                    <div className="entry-details">
                      <p className="entry-primary">{request.company}</p>
                      <p className="entry-secondary">{request.email}</p>
                    </div>
                    <span className="entry-timestamp">{request.time}</span>
                  </div>
                ))
              ) : (
                <p className="no-data-message">No demo requests available</p>
              )}
            </div>
            <div className="panel-footer">
              <button
                className="panel-view-all"
                onClick={() => handleRouteNavigation('/admin/demo-requests')}
              >
                View All Requests
              </button>
            </div>
          </div>

          {/* Pending Reviews Panel */}
          <div className="activity-panel">
            <div className="panel-header">
              <h3 className="panel-title">Pending Reviews</h3>
              <span className="panel-badge panel-badge-warning">{recentActivityData.pendingReviews?.length || 0}</span>
            </div>
            <div className="panel-content">
              {recentActivityData.pendingReviews && recentActivityData.pendingReviews.length > 0 ? (
                recentActivityData.pendingReviews.map((review) => (
                  <div
                    key={review.id}
                    className="activity-entry"
                    onClick={() => handleRouteNavigation(`/admin/reviews/${review.id}`)}
                    tabIndex={0}
                    role="button"
                  >
                    <div className="entry-details">
                      <p className="entry-primary">{review.product}</p>
                      <p className="entry-secondary">
                        {review.reviewer} • {'⭐'.repeat(review.rating)}
                      </p>
                    </div>
                    <span className="entry-timestamp">{review.time}</span>
                  </div>
                ))
              ) : (
                <p className="no-data-message">No pending reviews</p>
              )}
            </div>
            <div className="panel-footer">
              <button
                className="panel-view-all"
                onClick={() => handleRouteNavigation('/admin/draft-reviews')}
              >
                Review All Pending
              </button>
            </div>
          </div>

          {/* Recent Products Panel */}
          <div className="activity-panel">
            <div className="panel-header">
              <h3 className="panel-title">Recently Added Products</h3>
              <span className="panel-badge panel-badge-success">{recentActivityData.recentProducts?.length || 0}</span>
            </div>
            <div className="panel-content">
              {recentActivityData.recentProducts && recentActivityData.recentProducts.length > 0 ? (
                recentActivityData.recentProducts.map((product) => (
                  <div
                    key={product.id}
                    className="activity-entry"
                    onClick={() => handleRouteNavigation(`/admin/products/${product.id}`)}
                    tabIndex={0}
                    role="button"
                  >
                    <div className="entry-details">
                      <p className="entry-primary">{product.name}</p>
                      <p className="entry-secondary">{product.category}</p>
                    </div>
                    <span className="entry-timestamp">{product.time}</span>
                  </div>
                ))
              ) : (
                <p className="no-data-message">No recent products</p>
              )}
            </div>
          </div>
        </div>
      </section>
      <SecondaryFooter />
    </AdminLayout>
  );
};

export default AdminDashboard;